<template>
  <s-modal
    class="maintenance-action"
    v-model="showModal"
    cancel-confirm-message-code="MC006"
    footer-class="pb-1"
    :confirm-type="confirmationTypes.YES_NO"
    :size="modalSize"
    :ok-confirm-message-code="okConfirmCode"
    :ok-title="okTitle"
    :title="$t('engineering.commutator-maintenance.maintenance-action')"
    :ok-only="visualizationMode"
    :ok-promise="okPromise"
    :is-loading="showLoading"
    :ok-disabled="disableConfirm"
    @ok="closeModal"
    @cancel="cancel"
  >
    <b-alert
      class="maintenance-action__warning"
      v-html="warningMessage"
      variant="warning"
      :show="!visualizationMode"
    />
    <b-row class="maintenance-action__entities-wrapper">
      <b-col
        class="maintenance-action__company"
        cols="auto"
      >
        <span>
          {{ $t('entities.company') }}
        </span>
        <label>
          {{ maintenance.companyName }}
        </label>
      </b-col>
      <b-col
        class="maintenance-action__regional"
        cols="auto"
      >
        <span>
          {{ $t('entities.regional') }}
        </span>
        <label>
          {{ maintenance.regionalName }}
        </label>
      </b-col>
      <b-col
        class="maintenance-action__installation"
        cols="auto"
      >
        <span>
          {{ $t('entities.installation') }}
        </span>
        <label>
          {{ maintenance.installationName }}
        </label>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <label class="m-0">
          {{ $t('engineering.commutator-maintenance.equipment-data') }}:
        </label>
        <div class="maintenance-action__equipment-data">
          <div>
            <label class="m-0">
              {{ $t('entities.equipment') }}:
            </label>
            <span>{{ equipment.name }}</span>
          </div>
          <div>
            <label class="m-0">
              {{ $t('entities.commutator') }}:
            </label>
            <span>{{ equipment.commutatorId }}</span>
          </div>
        </div>
      </b-col>
    </b-row>
    <b-row class="d-flex justify-content-between mt-3">
      <b-col
        cols="auto"
      >
        <label>
          {{ $t('entities.user') }}:
        </label>
        <span>
          {{ maintenance.user }}
        </span>
      </b-col>
      <b-col
        cols="auto"
      >
        <label>
          {{ $t('general.date-time') }}:
        </label>
        <span>
          {{ getDate(maintenance.dateTime) | dateTimeFilter }}
        </span>
      </b-col>
    </b-row>
    <hr class="mt-1"/>
    <s-component-slider
      v-if="hasPendencies"
      :key="sliderKey"
      :items="maintenancePendencies"
      @previous="setActivePendency"
      @next="setActivePendency"
    >
      <template v-slot="{ item, index }">
        <maintenance-data
          class="maintenance-action__maintenance-pendencies-data"
          :maintenance="item"
          :maintenance-action="maintenanceAction[index]"
          :visualization-mode="visualizationMode"
          @update:maintenance-action="setMaintenanceActionForPendencies($event, index)"
        />
      </template>
    </s-component-slider>
    <maintenance-data
      v-else
      :maintenance="maintenance.individualMaintenance"
      :maintenance-action.sync="maintenanceAction"
      :visualization-mode="visualizationMode"
    />
    <hr class="mb-2"/>
    <div
      v-show="showPendencyLegend"
      class="maintenance-action__pendency-count"
    >
      <label class="mb-0">
        {{ $t('engineering.commutator-maintenance.pendency-legend', {
              current: activePendency,
              total: maintenancePendencies.length
           })
        }}
      </label>
    </div>
  </s-modal>
</template>

<script>
import MaintenanceData from '@/domain/components/installation/engineering/commutatorMaintenance/MaintenanceData';
import { CommutatorMaintenanceActions } from 'sigma-mappings';
import Service from '@/domain/services/installation/engineering/commutatorMaintenance/commutatorMaintenance';
import LoadingMixin from '@/services/loadingMixin';
import { mapGetters } from 'vuex';
import confirmationType from '@/constants/confirmationTypes';
import { getNow } from '@/services/dateTime/datetimeService';

export default {
  name: 'MaintenanceActionModal',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    maintenance: {
      type: Object,
      default: () => ({}),
    },
    visualizationMode: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  components: { MaintenanceData },
  mixins: [LoadingMixin],
  data: () => ({
    maintenanceAction: {},
    sliderKey: 0,
    activePendency: 1,
    confirmationTypes: confirmationType,
  }),
  computed: {
    ...mapGetters([
      'currentFontSize',
    ]),
    showModal: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    modalSize() {
      return this.currentFontSize.includes('18') ? 'xl' : 'lg';
    },
    disableConfirm() {
      const someInvalid = this.hasPendencies
        ? Object.values(this.maintenanceAction).some(action => action.$invalid)
        : this.maintenanceAction.$invalid;
      return !this.visualizationMode && someInvalid;
    },
    showLoading() {
      return this.loading || this.isLoading;
    },
    showPendencyLegend() {
      return this.hasPendencies && this.maintenancePendencies.length >= 2;
    },
    okConfirmCode() {
      return this.visualizationMode ? null : 'MC048';
    },
    okPromise() {
      return this.visualizationMode ? null : this.postMaintenanceActions;
    },
    okTitle() {
      return this.visualizationMode ? this.$t('general.ok') : this.$t('general.confirm');
    },
    hasPendencies() {
      return this.maintenancePendencies.length;
    },
    maintenancePendencies() {
      return this.maintenance.pendencies || [];
    },
    equipment() {
      return this.maintenance.equipment || {};
    },
    warningMessage() {
      return `<strong>${this.$t('messages.MI028.title')}</strong> ${this.$t('messages.MI028.message')}`;
    },
  },
  methods: {
    forceReRenderSlider() {
      this.sliderKey += 1;
    },
    setActivePendency(activeIndex) {
      this.activePendency = activeIndex + 1;
    },
    postMaintenanceActions() {
      const { assetModuleId } = this.$route.params;
      const { commutatorId } = this.maintenance.equipment;
      const payload = this.hasPendencies
        ? { pendencies: [...Object.values(this.maintenanceAction)] }
        : { individualMaintenance: this.maintenanceAction };
      this.startLoading();
      return Service.setCommutatorMaintenanceAction(
        assetModuleId,
        commutatorId,
        payload,
      ).then(() => {
        this.$pushAlert({ code: 'MS031' });
        this.$emit('ok');
        this.forceReRenderSlider();
        this.closeModal();
      }).finally(() => {
        this.endLoading();
      });
    },
    initializeMaintenanceAction() {
      this.maintenanceAction = {};
      if (this.hasPendencies) {
        this.setInitialMaintenanceActionValuesByPendencies();
        return;
      }
      this.setInitialMaintenanceActionValuesByIndividualMaintenance();
    },
    setInitialMaintenanceActionValuesByPendencies() {
      this.maintenance.pendencies.forEach((pendency, index) => {
        if (this.visualizationMode) {
          this.setMaintenanceActionForPendencies(pendency, index);
          return;
        }
        const identifiedByIed = pendency.iedData.maintenanceIdentifiedByIed;
        const newData = {
          ...pendency,
          duration: '00:30',
          maintenanceDate: identifiedByIed ? pendency.iedData.dateTime : getNow(),
          action: this.getActionByIdentification(identifiedByIed),
          resultExecutionId: pendency.iedData.resultExecutionId,
        };
        this.setMaintenanceActionForPendencies(newData, index);
      });
    },
    setInitialMaintenanceActionValuesByIndividualMaintenance() {
      const { individualMaintenance } = this.maintenance;
      if (this.visualizationMode) {
        this.maintenanceAction = individualMaintenance;
        return;
      }
      this.maintenanceAction = {
        ...individualMaintenance,
        duration: '00:30',
        maintenanceDate: getNow(),
        action: this.getActionByIdentification(false),
      };
    },
    getActionByIdentification(identifiedByIed) {
      return identifiedByIed
        ? CommutatorMaintenanceActions.IGNORE_AND_DONT_RESOLVES
        : CommutatorMaintenanceActions.CONFIRM_MAINTENANCE;
    },
    getDate(date) {
      return date || getNow();
    },
    setMaintenanceActionForPendencies(data, index) {
      this.$set(this.maintenanceAction, index, data);
    },
    closeModal() {
      this.showModal = false;
    },
    cancel() {
      this.initializeMaintenanceAction();
      this.closeModal();
    },
  },
  watch: {
    maintenance(newValue) {
      if (newValue) {
        this.initializeMaintenanceAction();
      }
    },
  },
};
</script>

<style lang="sass" scoped>
@import "src/styles/defaults/variables"

.maintenance-action
  &__warning
    background-color: $color-warning-light !important
  &__company
    display: flex
    flex-direction: column
    color: $color-secondary
  &__regional
    display: flex
    flex-direction: column
    color: var(--font-color-primary)
  &__installation
    display: flex
    flex-direction: column
    color: $color-primary
  &__entities-wrapper
    display: flex
    justify-content: space-between
  &__equipment-data
    border: 1px solid $color-border-gray
    padding: 5px 8px
    display: flex
    justify-content: space-between
  &__footer
    display: flex
    justify-content: space-between
    flex: 1
  &__pendency-count
    display: flex
    justify-content: center
  &__maintenance-pendencies-data
  ::v-deep
    .modal-body
      padding-top: 0
      padding-bottom: 8px
    .modal-footer
      padding-top: 0
      min-height: unset
</style>
